import React from 'react';
import { bool, func, number, string, array } from 'prop-types';
import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';
import cx from 'classnames';

import LineProgressBar from 'components/LineProgressBar';
import BaseTooltip from 'components/BaseTooltip';
import { CheckIcon, PlayerPlayIcon, PlayerPauseIcon } from 'components/Icon';
import MaterialDropdownButton from 'components/MaterialDropdownButton';
import useScrollIntoSelected from 'hooks/useScrollIntoSelected';

import OPEN_MODE from '../PlayList/components/ChapterList/constants';
import ChapterList from '../PlayList/components/ChapterList';

import styles from './LessonListItem.scss';

function LessonListItem({
  comingSoon,
  duration,
  estimatedUploadDate,
  isChapterListOpen,
  isCompleted,
  isLocked,
  isPlaying,
  isSelected,
  isTrailer,
  learningProgress,
  materials,
  name,
  onLessonClick,
  onLessonWrapperClick,
  onMaterialClick,
  remainingDurationMinute,
  userPurchased,
  hasChapters,
}) {
  useStyles(styles);

  const { t } = useTranslation(['course']);

  const lessonListItemRef = useScrollIntoSelected(
    isSelected && isPlaying && !isChapterListOpen,
  );

  const renderLessonStatus = () => {
    if (isCompleted) return <CheckIcon />;
    if (isSelected && isPlaying) return <PlayerPauseIcon />;

    return <PlayerPlayIcon />;
  };

  const renderDuration = () => {
    if (comingSoon) {
      const text = estimatedUploadDate
        ? t('videoplayer.release.date', { date: estimatedUploadDate })
        : t('videoplayer.release.comingSoon');
      return (
        <div className={styles.comingSoonWrapper}>
          <span className={styles.comingSoon}>{text}</span>
        </div>
      );
    }

    return <div className={styles.duration}>{duration}</div>;
  };

  const showMaterialButton = materials.length > 0;
  const showProgressBar = userPurchased && learningProgress !== 1 && isSelected;
  const showMaterialOrProgress =
    !isTrailer && (showMaterialButton || showProgressBar);

  const showChapters = isSelected && hasChapters;

  return (
    <li
      role="option"
      aria-selected={isSelected}
      className={styles.root}
      ref={lessonListItemRef}
      onClick={onLessonWrapperClick}
    >
      <div
        onClick={onLessonClick}
        className={cx(styles.lesson, {
          [styles.active]: isSelected,
          [styles.disabled]: comingSoon,
          [styles.finished]: isCompleted,
        })}
      >
        <div className={styles.titleRow}>
          <ul className={styles.statusWrapper}>
            <li className={styles.status}>{renderLessonStatus()}</li>
          </ul>
          <BaseTooltip overlay={name}>
            <div className={styles.name}>{name}</div>
          </BaseTooltip>
          {renderDuration()}
        </div>
        {showMaterialOrProgress && (
          <div className={styles.statusRow}>
            {showProgressBar && (
              <LineProgressBar
                progress={learningProgress}
                textStart={t('videoplayer.progressRemaining', {
                  minute: remainingDurationMinute,
                })}
                classNameMap={{ root: styles.progressBar }}
              />
            )}
            {showMaterialButton && (
              <MaterialDropdownButton
                btnText={t('videoplayer.material')}
                isDisabled={isLocked}
                materials={materials}
                onClickMaterial={onMaterialClick}
                className={styles.materialButton}
              />
            )}
          </div>
        )}
      </div>
      {showChapters && (
        <div className={styles.chapterListWrapper}>
          <ChapterList openMode={OPEN_MODE.DROP_DOWN} />
        </div>
      )}
    </li>
  );
}
LessonListItem.propTypes = {
  comingSoon: bool.isRequired,
  duration: string.isRequired,
  isCompleted: bool.isRequired,
  isTrailer: bool.isRequired,
  materials: array.isRequired,
  name: string.isRequired,
  onLessonClick: func.isRequired,
  onLessonWrapperClick: func.isRequired,
  onMaterialClick: func.isRequired,
  remainingDurationMinute: number.isRequired,
  userPurchased: bool.isRequired,
  estimatedUploadDate: string,
  isChapterListOpen: bool,
  isLocked: bool,
  isPlaying: bool,
  isSelected: bool,
  learningProgress: number,
  hasChapters: bool,
};

LessonListItem.defaultProps = {
  estimatedUploadDate: undefined,
  isChapterListOpen: false,
  isLocked: false,
  isPlaying: false,
  isSelected: false,
  learningProgress: undefined,
  hasChapters: false,
};
export default LessonListItem;
